import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useBillingUsage, useFlowHealthSettings, useSaveFlowHealthSettings } from "~/api/queries";
import { Metric, Pill, Row, Rows } from "~/features/automation/components";
import { compactTokens, formatBytes } from "~/features/automation/format";
import { Empty } from "~/foundation/components/Empty";
import { Input } from "~/foundation/components/Input";
import { Spinner } from "~/foundation/components/Spinner";
import { Switch } from "~/foundation/components/Switch";
import { Text } from "~/foundation/components/Text";
import { formatRelativeTime } from "~/foundation/utils/time";

const MIN_RETENTION_DAYS = 7;
const MAX_RETENTION_DAYS = 3650;

/** What automation costs this workspace, and how failures are announced — three switches, one report. */
export function FlowHealth() {
  const { t, i18n } = useTranslation();
  const { data: settings, isLoading } = useFlowHealthSettings();
  const save = useSaveFlowHealthSettings();
  const { data: usage, isLoading: usageLoading, isError: usageError } = useBillingUsage(30);

  const [retentionDraft, setRetentionDraft] = useState("");

  useEffect(() => {
    setRetentionDraft(settings?.flowsAuditRetentionDays?.toString() ?? "");
  }, [settings?.flowsAuditRetentionDays]);

  if (isLoading || !settings) {
    return <Spinner label={t("Loading")} />;
  }

  const commitRetention = () => {
    const trimmed = retentionDraft.trim();
    if (trimmed === "") {
      save.mutate({ flowsAuditRetentionDays: null });
      return;
    }
    const parsed = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(parsed) || parsed < MIN_RETENTION_DAYS || parsed > MAX_RETENTION_DAYS) {
      toast.error(t("Retention must be between 7 and 3650 days"));
      setRetentionDraft(settings.flowsAuditRetentionDays?.toString() ?? "");
      return;
    }
    save.mutate({ flowsAuditRetentionDays: parsed });
  };

  return (
    <div className="flex flex-col gap-5">
      <Text variant="small">
        {t(
          "Every flow run is audited, its failures are announced, and its AI and storage usage is recorded on the workspace ledger for future billing. These switches govern the whole workspace.",
        )}
      </Text>

      <div className="flex flex-col divide-y divide-divider">
        <Switch
          checked={settings.flowsNotifyOnFailure}
          onCheckedChange={(checked) => save.mutate({ flowsNotifyOnFailure: checked })}
          label={t("Announce run failures")}
          description={t(
            "When a run fails — or pauses after a failed step and waits on a human — notify the run's owner, or the whole workspace when no owner can be resolved. Individual members can still tune their own channels under Notifications.",
          )}
        />
        <Switch
          checked={settings.flowsBillingEnabled}
          onCheckedChange={(checked) => save.mutate({ flowsBillingEnabled: checked })}
          label={t("Record billable usage")}
          description={t(
            "Capture every terminal run's AI tokens and the workspace's stored bytes on the billing ledger. Turning this off stops new capture; it does not delete recorded usage.",
          )}
        />
        <div className="flex items-start justify-between gap-4 py-3">
          <div className="min-w-0">
            <label htmlFor="flow-health-retention" className="text-body text-text">
              {t("Run audit retention")}
            </label>
            <p className="mt-0.5 text-small text-text-secondary">
              {t(
                "Days a finished run's full record (steps, logs, artifacts) is kept before purging. Leave empty for the server default (90). Billing rows always outlive the runs they meter.",
              )}
            </p>
          </div>
          <Input
            id="flow-health-retention"
            type="number"
            min={MIN_RETENTION_DAYS}
            max={MAX_RETENTION_DAYS}
            value={retentionDraft}
            onChange={(event) => setRetentionDraft(event.target.value)}
            onBlur={commitRetention}
            className="w-24 shrink-0"
            placeholder={t("90")}
          />
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <h3 className="text-body font-medium text-text">{t("Usage")}</h3>
        {usageLoading ? (
          <Spinner label={t("Loading")} />
        ) : usageError || !usage ? (
          <Empty message={t("Usage could not be loaded — this view is admin-only.")} />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <Metric
                label={t("AI tokens, last {{days}} days", { days: usage.windowDays })}
                value={compactTokens(usage.ai.total)}
                hint={t("{{in}} in · {{out}} out", {
                  in: compactTokens(usage.ai.tokensIn),
                  out: compactTokens(usage.ai.tokensOut),
                })}
              />
              <Metric
                label={t("Run artifact storage")}
                value={formatBytes(usage.storage.artifactBytes)}
                hint={t("latest nightly snapshot")}
              />
              <Metric
                label={t("Attachment storage")}
                value={formatBytes(usage.storage.attachmentBytes)}
                hint={t("latest nightly snapshot")}
              />
            </div>

            <div className="flex flex-col gap-2">
              <h4 className="text-small font-medium text-text-secondary">
                {t("Recent run charges")}
              </h4>
              {usage.recentRuns.length ? (
                <Rows>
                  {usage.recentRuns.map((run) => (
                    <Row key={`${run.runId}-${run.occurredAt}`}>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-body text-text">
                          {run.name ?? run.runId.slice(0, 8)}
                        </p>
                      </div>
                      <Pill tone={run.status === "failed" ? "danger" : "neutral"}>
                        {run.status ?? t("purged")}
                      </Pill>
                      <span className="hidden w-20 text-small text-text-tertiary tabular-nums sm:inline">
                        {compactTokens(run.tokensIn)} {t("in")}
                      </span>
                      <span className="hidden w-20 text-small text-text-tertiary tabular-nums sm:inline">
                        {compactTokens(run.tokensOut)} {t("out")}
                      </span>
                      <Pill>{run.source === "run:ide" ? t("IDE") : t("Server")}</Pill>
                      <span className="hidden w-24 text-small text-text-tertiary md:inline">
                        {formatRelativeTime(run.occurredAt, i18n.language)}
                      </span>
                    </Row>
                  ))}
                </Rows>
              ) : (
                <Empty
                  message={t("No charges yet — the ledger fills as runs finish from now on.")}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
